import type {MobileAlgoContext, ReadOnlyExtensionAPI} from './types'
import type {ExtensionAPI} from '@wix/document-manager-core'
import type {RMApi} from '../rendererModel'
import type {StructureExtensionAPI} from '../structure'
import type {CompRef, Pointer} from '@wix/document-services-types'
import type {ThemeExtAPI} from '../theme/theme'
import type {PageExtensionAPI} from '../page'
import type {MobileAlgoExtensionApi} from './mobileAlgo'
import type {ComponentsMetadataAPI} from '../componentsMetadata/componentsMetadata'
import type {AccessibilityExtensionAPI} from '../accessibility/types'
import type {LayoutExtensionApi} from '../layout/layout'
import type {ManifestExtensionAPI} from '../builderComponents/manifest'
import {isHeaderSection} from '../componentsMetadata/utils/pageUtils'
import type {DataModelExtensionAPI} from '../dataModel/dataModel'
import {DATA_TYPES} from '../../constants/constants'
import type {EnvironmentExtensionAPI} from '../environment'
import type {TPAExtensionAPI} from '../tpa'
import type {ComponentDefinitionExtensionAPI} from '../componentDefinition'
import {OOI_CSM_TYPES} from '@wix/document-manager-utils'

const isOOIComponent = (extensionAPI: ExtensionAPI, compPointer: CompRef): boolean => {
    const {components} = extensionAPI as StructureExtensionAPI
    const {tpa} = extensionAPI as TPAExtensionAPI
    const {componentDefinition} = extensionAPI as ComponentDefinitionExtensionAPI
    const componentType = components.getComponentType(compPointer)

    if (componentDefinition.isRefComponent(componentType)) {
        return true
    }

    if (!tpa.isTpaByCompType(componentType)) {
        return false
    }

    const {dataModel} = extensionAPI as DataModelExtensionAPI
    const compData = dataModel.components.getItem(compPointer, DATA_TYPES.data)
    const {appDefinitionId, widgetId} = compData ?? {}
    if (!appDefinitionId || !widgetId) {
        return false
    }

    const {rendererModel} = extensionAPI as RMApi
    const csm = rendererModel.getClientSpecMap()
    const appData = Object.values(csm).find((entry: any) => entry?.appDefinitionId === appDefinitionId)
    const csmComponent = (appData as any)?.components?.find((c: any) => c.componentId === widgetId)

    return OOI_CSM_TYPES.has(csmComponent?.type)
}

const isCustomElementComponent = (extensionAPI: ExtensionAPI, compPointer: CompRef): boolean => {
    const {components} = extensionAPI as StructureExtensionAPI
    const componentType = components.getComponentType(compPointer)
    return componentType === 'wixui.CustomElementComponent'
}

export const createReadOnlyExtensionApi = (extensionAPI: ExtensionAPI): ReadOnlyExtensionAPI => {
    return {
        getInstance: () => (extensionAPI as RMApi).siteAPI.getInstance(),
        getChildren: (componentPointer: Pointer) =>
            (extensionAPI as StructureExtensionAPI).components.getChildren(componentPointer),
        isTemplate: () => (extensionAPI as RMApi).siteAPI.isTemplate(),
        getTextTheme: () => (extensionAPI as ThemeExtAPI).theme.getTextTheme(),
        getMetaSiteId: () => (extensionAPI as RMApi).rendererModel.getMetaSiteId(),
        getSiteId: () => (extensionAPI as RMApi).rendererModel.getSiteId(),
        getMainPageId: () => (extensionAPI as PageExtensionAPI).page.getMainPageId(),
        getPluginsApi: (ctx: MobileAlgoContext) =>
            (extensionAPI as MobileAlgoExtensionApi).mobileAlgo.plugins.getApis(ctx),
        components: {
            isPinned: (componentPointer: Pointer) =>
                (extensionAPI as LayoutExtensionApi).components.isPinned(componentPointer),
            isHeaderSection: (componentPointer: Pointer) => isHeaderSection(extensionAPI, componentPointer)
        },
        isFullWidth: (componentPointer: Pointer) =>
            (extensionAPI as ComponentsMetadataAPI).componentsMetadata.isFullWidth(componentPointer),
        isRTL: () =>
            (extensionAPI as AccessibilityExtensionAPI).accessibility.languageDirection.getSiteDirection() === 'rtl',
        layout: {
            isContentHeight: (componentPointer: Pointer) =>
                (extensionAPI as ManifestExtensionAPI).manifest.layout.isContentHeight(componentPointer as CompRef) ||
                isOOIComponent(extensionAPI, componentPointer as CompRef) ||
                isCustomElementComponent(extensionAPI, componentPointer as CompRef),
            isContentWidth: (componentPointer: Pointer) =>
                (extensionAPI as ManifestExtensionAPI).manifest.layout.isContentWidth(componentPointer as CompRef),
            isStretchedHeight: (componentPointer: Pointer) =>
                (extensionAPI as ManifestExtensionAPI).manifest.layout.isStretchedHeight(componentPointer as CompRef),
            isStretchedWidth: (componentPointer: Pointer) =>
                (extensionAPI as ManifestExtensionAPI).manifest.layout.isStretchedWidth(componentPointer as CompRef),
            shouldPreserveAspectRatio: (componentPointer: Pointer) =>
                (extensionAPI as ManifestExtensionAPI).manifest.layout.shouldPreserveAspectRatio(
                    componentPointer as CompRef
                )
        },
        manifest: (extensionAPI as ManifestExtensionAPI).manifest,
        getMainTextField: (componentType: string, data: any) =>
            (extensionAPI as ManifestExtensionAPI).manifest.getMainTextField(componentType, data),
        isDuplicatedComponent: (compPointer: Pointer, rootPointer: Pointer): boolean => {
            const {dataModel} = extensionAPI as DataModelExtensionAPI
            const editorSettings = dataModel.components.getItem(rootPointer, DATA_TYPES.editorsettings)
            return !!editorSettings?.linkedComponents?.[compPointer.id]
        },
        hasDuplicatedComponent: (rootPointer: Pointer): boolean => {
            const {dataModel} = extensionAPI as DataModelExtensionAPI
            const editorSettings = dataModel.components.getItem(rootPointer, DATA_TYPES.editorsettings)
            return Object.values(editorSettings?.linkedComponents || {}).length >= 1
        },
        isClonedComponent: (compPointer: Pointer, rootPointer: Pointer): boolean => {
            const {dataModel} = extensionAPI as DataModelExtensionAPI
            const editorSettings = dataModel.components.getItem(rootPointer, DATA_TYPES.editorsettings)
            return (
                !!editorSettings?.linkedComponents &&
                Object.values(editorSettings.linkedComponents).includes(compPointer.id)
            )
        },
        experiments: {
            isOpen: (experiment: string) =>
                (extensionAPI as EnvironmentExtensionAPI).environment.isExperimentOpen(experiment)
        }
    }
}
